import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import InvoiceBatch from "../../../models/InvoiceBatch";
import { formatCurrencyString } from "../../../utils/string.utils";
import AppText from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";

// Hard coded key in "by service" breakdown for deduction
const DEDUCTION_KEY = "deduction";
interface InvoiceJournalProps {
	invoiceBatch: InvoiceBatch;
	inputRef?: any;
}

const InvoiceJournal: React.FC<InvoiceJournalProps> = ({
	invoiceBatch: { metadata, to, invoiceDate, pool, invoices, total, totalVat, status },
	inputRef,
}) => {
	const { t } = useTranslation();

	const includedInvoices = invoices.filter((invoice) => invoice.include);

	const groupedByService = useMemo(() => {
		const map = new Map<
			string,
			{ total: number; totalExclVat: number; service: string; branch: string }
		>();

		for (const { invoice } of includedInvoices) {
			for (const row of invoice.invoiceRows) {
				// Deduction does not have any service/branch but still needs to be
				// part of breakdown
				if (row.isDeduction) {
					const deduction = map.get(DEDUCTION_KEY);
					// const totalExclVat = row.total - row.vatPercentage * row.total;

					if (deduction) {
						deduction.total += row.total || 0;
						deduction.totalExclVat += row.totalExcludingVat || 0;
					} else {
						map.set(DEDUCTION_KEY, {
							total: row.total || 0,
							totalExclVat: row.totalExcludingVat || 0,
							branch: DEDUCTION_KEY,
							service: DEDUCTION_KEY,
						});
					}
				}

				if (!row.service || !row.branch) continue;

				const key = row.service + "_" + row.branch;

				const serviceTotal = map.get(key);

				if (serviceTotal) {
					serviceTotal.total += row.total || 0;
					serviceTotal.totalExclVat += row.totalExcludingVat || 0;
				} else {
					map.set(key, {
						total: row.total || 0,
						totalExclVat: row.totalExcludingVat || 0,
						service: row.service,
						branch: row.branch,
					});
				}
			}
		}

		return Array.from(map.values()).sort((a, b) => {
			if (a.service === DEDUCTION_KEY) {
				return 1;
			} else if (b.service === DEDUCTION_KEY) {
				return -1;
			}
			return a.service.localeCompare(b.service);
		});
	}, [includedInvoices]);

	return (
		<StyledJournalViewer ref={inputRef}>
			<AppText margin="0 0 20px 0" size="l" weight="bold">
				{t("invoice.journal.invoiceJournal")}&nbsp;
				{moment(metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE)}
			</AppText>
			<Flex margin="0 0 7px 0">
				<AppText color="black">{t("invoice.journal.to")}:</AppText>
				<AppText color="black">
					&nbsp;
					{moment(to).format(DATE_TIME_FORMAT_TYPES.DATE)}
				</AppText>
			</Flex>

			<Flex margin="0 0 7px 0">
				<AppText color="black">{t("invoice.journal.invoiceDate")}:</AppText>
				<AppText color="black">
					&nbsp;
					{moment(invoiceDate).format(DATE_TIME_FORMAT_TYPES.DATE)}
				</AppText>
			</Flex>
			<Flex margin="0 0 7px 0">
				<AppText color="black">{t("POOL")}:</AppText>
				<AppText color="black">&nbsp; {pool || t("ALL")}</AppText>
			</Flex>
			<Flex margin="0 0 40px 0">
				<AppText color="black">{t("invoice.journal.numInvoices")}:</AppText>
				<AppText color="black">&nbsp; {includedInvoices.length}</AppText>
			</Flex>

			<table className="invoices">
				<thead>
					<tr>
						{status === "PROCESSED" && (
							<th style={{ width: 100 }}>{t("invoice.journal.invoiceNum")}</th>
						)}
						<th style={{ width: 240 }}>{t("invoice.journal.customer")}</th>
						<th style={{ width: 100 }}>{t("invoice.journal.pool")}</th>
						<th>{t("invoice.journal.dueDate")}</th>
						<th>{t("invoice.journal.exclVAT")}</th>
						<th>{t("invoice.journal.inclVAT")}</th>
					</tr>
				</thead>
				<tbody>
					{includedInvoices.map(({ invoice }) => (
						<tr key={invoice.id}>
							{status === "PROCESSED" && <td>{invoice.invoiceNumber}</td>}
							<td>
								{invoice.invoiceName} ({invoice.customerNumber})
							</td>
							<td>{invoice.pool || "-"}</td>
							<td>{moment(invoice.dueDate).format("YYYY-MM-DD")}</td>
							<td>{formatCurrencyString(invoice.total - invoice.totalVAT)}</td>
							<td>{formatCurrencyString(invoice.total)}</td>
						</tr>
					))}
				</tbody>
			</table>

			<AppText margin="3rem 0 1rem 0" weight="bold">
				{t("invoice.journal.perService")}
			</AppText>

			<table className="service-breakdown">
				<thead>
					<tr>
						<th>{t("SERVICE")}</th>
						<th style={{ width: 120 }}>{t("invoice.journal.exclVAT")}</th>
						<th style={{ width: 150 }}>{t("invoice.journal.inclVAT")}</th>
					</tr>
				</thead>
				<tbody>
					{groupedByService.map(({ branch, service, total, totalExclVat }) => (
						<tr key={service + branch}>
							<td>{service === DEDUCTION_KEY ? "Avdrag" : service}</td>
							<td>{formatCurrencyString(totalExclVat)}</td>
							<td>{formatCurrencyString(total)}</td>
						</tr>
					))}
					<tr className="total-row">
						<td>{t("TOTAL")}</td>
						<td>{formatCurrencyString(total - totalVat)}</td>
						<td>{formatCurrencyString(total)}</td>
					</tr>
				</tbody>
			</table>
		</StyledJournalViewer>
	);
};

export default InvoiceJournal;

const StyledJournalViewer = styled.div`
	width: 837px;
	padding: 30px 50px 50px 50px;
	background: ${({ theme }) => theme.colors.COLOR_WHITE};
	table {
		border-collapse: collapse;
		width: 100%;

		thead {
			th {
				font-weight: ${({ theme }) => theme.fontWeight.FONT_WEIGHT_MEDIUM};
				font-size: ${({ theme }) => theme.fontSizes.FONT_SIZE_S};
				text-align: left;
				padding-bottom: 0.25rem;
			}
		}

		tbody,
		tfoot {
			th,
			td {
				font-weight: ${({ theme }) => theme.fontWeight.FONT_WEIGHT_REGULAR};
				font-size: ${({ theme }) => theme.fontSizes.FONT_SIZE_S};
				padding: 0.25rem 0;
				text-align: left;
			}
		}
	}

	.invoices {
		th:nth-last-child(1),
		th:nth-last-child(2),
		td:nth-last-child(1),
		td:nth-last-child(2) {
			text-align: right;
		}
	}

	.service-breakdown {
		thead th:not(:first-child),
		tbody td:not(:first-child) {
			text-align: right;
		}

		tbody tr:nth-last-child(2) td {
			padding-bottom: 0.6rem;
		}

		.total-row td {
			padding-top: 0.6rem;
			border-top: 1px solid black;
			font-size: ${({ theme }) => theme.fontSizes.FONT_SIZE_M};
		}
	}
`;
